import React, { useState } from 'react';
import { ArrowLeft, Send, CheckCircle, Car, Euro, Calendar, Clock, Mail, CreditCard, Building2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import axios from 'axios';
import { getAuthHeader } from '../../services/otpAuthService';

interface Studio {
  _id: string;
  ragioneSociale: string;
  citta: string;
  provincia: string;
  isSecondarySede?: boolean;
  parentMemberId?: string;
  distanza?: number;
  associateId?: {
    email?: string;
    nominativo?: string;
  };
  email?: string;
}

interface DatiAtto {
  targa: string;
  prezzoVendita: number;
  soggettoIVA: boolean;
  tipoAtto: 'art56' | 'normale';
  dataAppuntamento: string;
  oraAppuntamento: string;
  modalitaPagamento: 'venditore' | 'bonifico';
  emailVenditore: string;
}

interface StepRiepilogoProps {
  studiSelezionati: Studio[];
  datiAtto: DatiAtto;
  onBack: () => void;
  onSuccess: () => void;
}

const StepRiepilogo: React.FC<StepRiepilogoProps> = ({ studiSelezionati, datiAtto, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [noteRichiedente, setNoteRichiedente] = useState('');

  // URL base per API REST (senza /graphql)
  const toast = useToast();
  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const tipoAttoLabels = {
    art56: 'Trasferimento - Art. 56',
    mini: 'Mini Passaggio',
    normale: 'Trasferimento di proprietà'
  };

  const modalitaPagamentoLabels = {
    venditore: 'Pagato dal Venditore',
    bonifico: 'Bonifico Istantaneo'
  };

  const handleInviaRichiesta = async () => {
    setLoading(true);

    try {
      const payload = {
        studiSelezionati: studiSelezionati.map(s => s._id),
        targa: datiAtto.targa,
        prezzoVendita: datiAtto.prezzoVendita,
        soggettoIVA: datiAtto.soggettoIVA,
        tipoAtto: datiAtto.tipoAtto,
        dataAppuntamento: datiAtto.dataAppuntamento,
        oraAppuntamento: datiAtto.oraAppuntamento,
        modalitaPagamento: datiAtto.modalitaPagamento,
        emailVenditore: datiAtto.emailVenditore,
        noteRichiedente: noteRichiedente.trim() || undefined
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/atti-svincolo`,
        payload,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success('Richiesta inviata con successo!');
        onSuccess();
      } else {
        toast.error(response.data.message || 'Errore durante l\'invio');
      }
    } catch (error: any) {
      console.error('Errore invio richiesta:', error);
      console.error('Dettagli errore:', error.response?.data);
      toast.error(error.response?.data?.message || 'Errore durante l\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2">
          Riepilogo Richiesta
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Verifica i dati prima di inviare la richiesta
        </p>
      </div>

      {/* Studi Selezionati */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-600" />
          Studi Selezionati ({studiSelezionati.length})
        </h3>
        <div className="space-y-3">
          {studiSelezionati.map((studio, index) => {
            const emailStudio = studio.associateId?.email || studio.email;
            return (
              <div key={studio._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {index + 1}. {studio.ragioneSociale}
                  </p>
                  {studio.isSecondarySede && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      L'atto si svolgerà in questa sede
                    </p>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {studio.citta} ({studio.provincia})
                    {studio.distanza && ` - ${studio.distanza.toFixed(1)} km`}
                  </p>
                  {emailStudio && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {emailStudio}
                    </p>
                  )}
                </div>
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dati Veicolo */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Car className="w-5 h-5 text-purple-600" />
          Dati Veicolo
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Targa</p>
            <p className="font-semibold text-gray-900 dark:text-white">{datiAtto.targa}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Prezzo di Vendita</p>
            <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
              <Euro className="w-4 h-4" />
              {datiAtto.prezzoVendita.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Soggetto IVA</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {datiAtto.soggettoIVA ? 'Sì' : 'No'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Tipo Atto</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {tipoAttoLabels[datiAtto.tipoAtto]}
            </p>
          </div>
        </div>
      </div>

      {/* Appuntamento */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          Appuntamento
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Data</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatDate(datiAtto.dataAppuntamento)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ora</p>
            <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {datiAtto.oraAppuntamento}
            </p>
          </div>
        </div>
      </div>

      {/* Pagamento */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-purple-600" />
          Modalità di Pagamento
        </h3>
        <p className="font-semibold text-gray-900 dark:text-white">
          {modalitaPagamentoLabels[datiAtto.modalitaPagamento]}
        </p>
        {datiAtto.modalitaPagamento === 'bonifico' && (
          <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
            ⚠️ Ricorda di effettuare il bonifico istantaneo prima di caricare i documenti
          </p>
        )}
      </div>

      {/* Dati Venditore */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-purple-600" />
          Dati Venditore
        </h3>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
          <p className="font-semibold text-gray-900 dark:text-white">{datiAtto.emailVenditore}</p>
        </div>
      </div>

      {/* Note per gli studi */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5 flex items-center gap-2">
          <Mail className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
          Note per gli studi
          <span className="text-xs font-normal text-gray-400">(opzionale)</span>
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Queste note saranno visibili a tutti gli studi selezionati come primo messaggio nella chat della pratica.
        </p>
        <textarea
          value={noteRichiedente}
          onChange={(e) => setNoteRichiedente(e.target.value)}
          rows={3}
          maxLength={1000}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white resize-none"
          placeholder="Es: Necessario passaggio urgente entro fine mese, il veicolo si trova a Roma..."
        />
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">{noteRichiedente.length}/1000</p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
          Cosa succede dopo?
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <li>• Gli studi selezionati riceveranno una notifica via email</li>
          <li>• Potranno visualizzare i dettagli e inviare un preventivo</li>
          <li>• Riceverai una notifica per ogni preventivo ricevuto</li>
          <li>• Potrai scegliere il preventivo migliore e caricare i documenti</li>
          <li>• Il venditore riceverà i dettagli dell'appuntamento via email</li>
        </ul>
      </div>

      {/* Bottoni */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>

        <button
          type="button"
          onClick={handleInviaRichiesta}
          disabled={loading}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Invio in corso...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Invia Richiesta
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StepRiepilogo;

